import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Text,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Button,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  doc,
  getDoc,
  setDoc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../firebase/firebase";
import axios from "axios";
import CustomDropdown from "../../../components/CustomDropdown";
export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  phone: string;
  assignedProjects?: string[];
  assignedTasks?: { id: string; name: string }[];
  organizationId: string;
};

interface StrikeUserProps {
  color: string;
  getNewSectionHeight: () => number;
  users: User[];
  entries: {
    id: string;
    name: string;
    status?: string;
    assignedUsers?: string[];
  }[];
  organizationId: string;
  projectId: string;
  channelId: string;
  taskId: string;
  isAdmin: boolean;
}

const StrikeUser: React.FC<StrikeUserProps> = ({
  color,
  getNewSectionHeight,
  entries,
  users,
  organizationId,
  projectId,
  channelId,
  taskId,
  isAdmin,
}) => {
  // Extracted hooks
  const borderColor = useColorModeValue("#E8C16C", "rgba(255,255,255,0.18)");
  const bg = useColorModeValue("white", "#14161B");
  const textColor = useColorModeValue("gray.600", "#C3C9D3");
  const borderColor2 = useColorModeValue("gray.300", "rgba(255,255,255,0.18)");
  const isDarkForDropdown = useColorModeValue(false, true);
  const userNameColor = useColorModeValue("#2D3748", "#FFFFFF");
  const modalBg = useColorModeValue("white", "#14161B");
  const modalBorderColor = useColorModeValue("#E8C16C", "rgba(255,255,255,0.18)");
  const modalCloseColor = useColorModeValue("#1A1C1F", "#E2E8F0");
  const textAreaColor = useColorModeValue("#2D3748", "#FFFFFF");
  const textAreaBg = useColorModeValue("#FFFFFF", "#1A1C1F");
  const textAreaCaret = useColorModeValue("#2D3748", "#FFFFFF");
  const cancelButtonColor = useColorModeValue("#4A5568", "#E2E8F0");
  const backendUrl = "https://api-xk2q5lbbua-uc.a.run.app";

  const [selectedEntryId, setSelectedEntryId] = useState<string | undefined>(
    entries?.[0]?.id
  );


  const { isOpen, onOpen, onClose } = useDisclosure();
  const [pendingLevel, setPendingLevel] = useState<1 | 2 | 3 | null>(null);
  const [pendingUser, setPendingUser] = useState<string | null>(null);
  const [pendingRemark, setPendingRemark] = useState("");
  // Force-disable flags when applying strike to ALL users for current task
  const [allApplied, setAllApplied] = useState<{
    1: boolean;
    2: boolean;
    3: boolean;
  }>({ 1: false, 2: false, 3: false });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    // Reset selection if entries changed or current selection is no longer valid
    if (!selectedEntryId || !entries.some((e) => e.id === selectedEntryId)) {
      setSelectedEntryId(entries?.[0]?.id);
    }
    // Reset global flags when task changes
    setAllApplied({ 1: false, 2: false, 3: false });
  }, [entries, selectedEntryId]);

  const selectedEntry = useMemo(
    () => entries.find((e) => e.id === selectedEntryId),
    [entries, selectedEntryId]
  );
  const isCompleted =
    (selectedEntry?.status || "").toLowerCase() === "completed";

  // Build assignees with stable userId keys
  const assignees = useMemo(() => {
    const ids = selectedEntry?.assignedUsers || [];
    return ids
      .map((id) => users.find((u) => u.id === id))
      .filter((u): u is User => Boolean(u))
      .map((u) => ({
        id: u.id,
        name: `${u.firstName} ${u.lastName || ""}`.trim(),
      }));
  }, [selectedEntry, users]);

  // Strike levels per assignee (0-3). Keyed by userId.
  const [strikeLevelsByUser, setStrikeLevelsByUser] = useState<
    Record<string, number>
  >({});
  const [remarksByUser, setRemarksByUser] = useState<
    Record<string, { 1?: string; 2?: string; 3?: string }>
  >({});

  useEffect(() => {
    // Ensure state contains keys for visible users
    if (assignees.length > 0) {
      // default zeros for this entry only
      const base: Record<string, number> = {};
      assignees.forEach((a) => (base[a.id] = 0));
      setStrikeLevelsByUser(base);
    } else {
      setStrikeLevelsByUser({});
    }
  }, [assignees]);

  // Fetch strikes meta for this task when selectedEntryId changes
  useEffect(() => {
    const loadStrikes = async () => {
      if (!selectedEntryId) return;
      const metaRef = doc(
        db,
        `organization/${organizationId}/projects/${projectId}/channels/${channelId}/tasks/${taskId}/strikes/${selectedEntryId}`
      );
      const snap = await getDoc(metaRef);
      // Build base (zeros) for this entry's assignees
      const base: Record<string, number> = {};
      assignees.forEach((a) => (base[a.id] = 0));
      if (snap.exists()) {
        const data = snap.data() as any;
        const incoming: Record<string, number> = {};
        if (data?.levels) {
          Object.keys(data.levels).forEach((k) => {
            const v = (data.levels as any)[k];
            incoming[k] = typeof v === "number" ? v : Number(v) || 0;
          });
        }
        // Replace with base overlaid by fetched to avoid leaking across entries
        setStrikeLevelsByUser({ ...base, ...incoming });
        if (data?.remarks)
          setRemarksByUser(
            data.remarks as Record<
              string,
              { 1?: string; 2?: string; 3?: string }
            >
          );
      } else {
        // no doc yet; use base only
        setStrikeLevelsByUser(base);
        setRemarksByUser({});
      }
    };
    loadStrikes();
    // reset applied flags per task
    setAllApplied({ 1: false, 2: false, 3: false });
  }, [
    selectedEntryId,
    organizationId,
    projectId,
    channelId,
    taskId,
    assignees,
  ]);

  const setStrike = (userId: string, level: 1 | 2 | 3) => {
    setStrikeLevelsByUser((prev) => ({ ...prev, [userId]: level }));
  };

  // Apply strike level to everyone (bulk for all)
  const setAllTo = (level: 1 | 2 | 3) => {
    setStrikeLevelsByUser((prev) => {
      const next: Record<string, number> = { ...prev };
      assignees.forEach((a) => {
        const current = next[a.id] || 0;
        next[a.id] = Math.max(current, level);
      });
      return next;
    });
    setAllApplied((prev) => ({ ...prev, [level]: true } as typeof prev));
  };

  const allHaveS1 =
    assignees.length > 0 &&
    assignees.every((a) => (strikeLevelsByUser[a.id] || 0) >= 1);
  const allHaveS2 =
    assignees.length > 0 &&
    assignees.every((a) => (strikeLevelsByUser[a.id] || 0) >= 2);
  const allHaveS3 =
    assignees.length > 0 &&
    assignees.every((a) => (strikeLevelsByUser[a.id] || 0) >= 3);

  // Check empty state
  const isEmpty = !entries || entries.length === 0;

  if (isEmpty) {
    return (
      <div>
        {" "}
        <Box
          border="1px solid"
          borderColor={borderColor}
          borderRadius="xl"
          p={4}
          boxShadow="sm"
          bg={bg}
          height={`${getNewSectionHeight()}px`}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <Text fontSize="sm" color={textColor} fontStyle="italic">
            No tasks available
          </Text>
        </Box>
      </div>
    );
  }

  return (
    <div>
      {" "}
      <Box
        border="1px solid"
        borderColor={borderColor2}
        borderRadius="xl"
        p={4}
        bg={bg}
        boxShadow="sm"
        height={`${getNewSectionHeight() + 30}px`}
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        alignItems="stretch"
      >
        <Box display={"flex"} alignItems={"center"}>
          <Text
            minW={"100px"}
            fontWeight="semibold"
            fontSize="sm"
            mb={2}
            color={color}
          >
            Select task :
          </Text>

          <CustomDropdown
            value={selectedEntryId || ''}
            onChange={(value) => setSelectedEntryId(value)}
            // Hooks moved to top
            isDark={isDarkForDropdown}
            options={entries.map((e) => ({ value: e.id, label: e.name }))}
            className="text-sm rounded-md border"
            style={{ height: "32px", width: "620px", boxSizing: "border-box", padding: "0 8px", marginTop: "-8px" }}
          />
        </Box>

        {assignees.length === 0 ? (
          <Box flex={1} display="flex" alignItems="center" justifyContent="center">
            <Text fontSize="sm" color="gray.500" fontStyle="italic">
              No users assigned
            </Text>
          </Box>
        ) : (
          <>
            <div className="flex items-center mb-2">
              <Text fontWeight="semibold" fontSize="sm" color={color}>
                Assigned Users
              </Text>
              {isCompleted && (
                <span className="ml-3 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs">
                  Completed
                </span>
              )}
              {selectedEntry?.status && !isCompleted && (
                <span
                  className={`ml-3 px-2 py-0.5 rounded-full text-[11px] ${(selectedEntry?.status || "").toLowerCase() === "in-progress" ||
                    (selectedEntry?.status || "").toLowerCase() === "started"
                    ? "bg-blue-100 text-blue-700"
                    : (selectedEntry?.status || "").toLowerCase() === "delay"
                      ? "bg-yellow-100 text-yellow-700"
                      : (selectedEntry?.status || "").toLowerCase() ===
                        "not-started"
                        ? "bg-red-100 text-red-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                >
                  {selectedEntry?.status}
                </span>
              )}
              {isAdmin && (
                <div className="ml-auto flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (allHaveS1) return;
                      setPendingLevel(1);
                      setPendingUser(null);
                      setPendingRemark("");
                      onOpen();
                    }}
                    disabled={allHaveS1 || isCompleted}
                    className={`px-2 py-1 rounded-full text-xs transition ${allHaveS1 || isCompleted
                      ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                      : "bg-[#FFF1CC] hover:bg-[#FFE8B3] text-[#B59143]"
                      }`}
                  >
                    Strike S1 (all)
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!allHaveS1) return;
                      setPendingLevel(2);
                      setPendingUser(null);
                      setPendingRemark("");
                      onOpen();
                    }}
                    disabled={!allHaveS1 || allApplied[2] || isCompleted || allHaveS2}
                    className={`px-2 py-1 rounded-full text-xs transition ${allHaveS1 && !allApplied[2] && !isCompleted && !allHaveS2
                      ? "bg-[#FFF1CC] hover:bg-[#FFE8B3] text-[#B59143]"
                      : "bg-gray-50 text-gray-400 cursor-not-allowed"
                      }`}
                  >
                    Strike S2 (all)
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!allHaveS2) return;
                      setPendingLevel(3);
                      setPendingUser(null);
                      setPendingRemark("");
                      onOpen();
                    }}
                    disabled={!allHaveS2 || allApplied[3] || isCompleted || allHaveS3}
                    className={`px-2 py-1 rounded-full text-xs transition ${allHaveS2 && !allApplied[3] && !isCompleted && !allHaveS3
                      ? "bg-[#FFE8B3] hover:bg-[#FFD98A] text-[#8C6F2E]"
                      : "bg-gray-50 text-gray-400 cursor-not-allowed"
                      }`}
                  >
                    Strike S3 (all)
                  </button>
                </div>
              )}
            </div>

            <div className="max-h-64 overflow-y-auto pr-1">
              <ul className="divide-y divide-gray-200">
                {assignees.map((a) => {
                  const level = strikeLevelsByUser[a.id] || 0;
                  const canS2 = level >= 1;
                  const canS3 = level >= 2;
                  return (
                    <li key={a.id} className="py-2 flex items-center gap-3">
                      <div className="w-8 h-8 text-xs rounded-full bg-[#FFF1CC] text-[#B59143] flex items-center justify-center font-semibold">
                        {a.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm" style={{ color: userNameColor }}>{a.name}</span>
                      <div className="ml-auto flex items-center gap-5">
                        <button
                          type="button"
                          aria-label="Strike 1"
                          disabled
                          className={`px-2 py-1 rounded-full text-xs transition ${level >= 1
                            ? "bg-red-100 text-red-700"
                            : "bg-gray-50 text-gray-400 cursor-not-allowed"
                            }`}
                        >
                          S1
                        </button>
                        <button
                          type="button"
                          aria-label="Strike 2"
                          onClick={() => {
                            if (!canS2 || level >= 2 || isCompleted) return;
                            setPendingLevel(2);
                            setPendingUser(a.id);
                            setPendingRemark("");
                            onOpen();
                          }}
                          disabled={
                            !canS2 || level >= 2 || allApplied[2] || isCompleted
                          }
                          className={`px-2 py-1 rounded-full text-xs transition ${level >= 2
                            ? "bg-red-200 text-red-800 cursor-not-allowed"
                            : canS2 && !allApplied[2] && !isCompleted
                              ? "bg-[#FFF1CC] hover:bg-[#FFE8B3] text-[#B59143]"
                              : "bg-gray-50 text-gray-400 cursor-not-allowed"
                            }`}
                        >
                          S2
                        </button>
                        <button
                          type="button"
                          aria-label="Strike 3"
                          onClick={() => {
                            if (!canS3 || level >= 3 || isCompleted) return;
                            setPendingLevel(3);
                            setPendingUser(a.id);
                            setPendingRemark("");
                            onOpen();
                          }}
                          disabled={
                            !canS3 || level >= 3 || allApplied[3] || isCompleted
                          }
                          className={`px-2 py-1 rounded-full text-xs transition ${level >= 3
                            ? "bg-red-300 text-red-900 cursor-not-allowed"
                            : canS3 && !allApplied[3] && !isCompleted
                              ? "bg-[#FFE8B3] hover:bg-[#FFD98A] text-[#8C6F2E]"
                              : "bg-gray-50 text-gray-400 cursor-not-allowed"
                            }`}
                        >
                          S3
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </>
        )}

        {/* Unified Modal for S1/S2/S3 (all or per-user) */}
        <Modal
          isOpen={isOpen}
          onClose={() => {
            onClose();
            setPendingRemark("");
            setPendingLevel(null);
            setPendingUser(null);
          }}
          isCentered
        >
          <ModalOverlay />
          <ModalContent bg={modalBg} border="1px solid" borderColor={modalBorderColor}>
            <ModalHeader color={color}>
              {pendingLevel ? `Strike S${pendingLevel} Remark` : "Remark"}
            </ModalHeader>
            <ModalCloseButton color={modalCloseColor} />
            <ModalBody>
              <div className="space-y-2">
                {/* Warning banner */}
                {pendingLevel && (
                  <div className="mb-2 rounded-md border border-yellow-300 bg-yellow-50 text-yellow-800 px-3 py-2 text-xs">
                    You are about to issue{" "}
                    <span className="font-semibold">
                      Strike S{pendingLevel}
                    </span>
                    . This will affect access to higher strikes and will disable
                    the respective button.
                    <span className="block text-[11px] text-yellow-700 mt-1 font-semibold">
                      This action cannot be undone here.
                    </span>
                  </div>
                )}
                {pendingUser && (
                  <div className="text-xs text-gray-600">
                    User:{" "}
                    <span className="font-medium text-gray-800">
                      {assignees.find((x) => x.id === pendingUser)?.name ||
                        pendingUser}
                    </span>
                  </div>
                )}
                <label className="block text-sm ">Remark</label>
                <textarea
                  value={pendingRemark}
                  onChange={(e) => setPendingRemark(e.target.value)}
                  className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#B59143]"
                  style={{
                    color: textAreaColor,
                    backgroundColor: textAreaBg,
                    caretColor: textAreaCaret,
                  }}
                  placeholder="Enter remark..."
                  rows={4}
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="ghost"
                mr={3}
                color={cancelButtonColor}
                onClick={() => {
                  onClose();
                  setPendingRemark("");
                  setPendingLevel(null);
                  setPendingUser(null);
                }}
              >
                Cancel
              </Button>
              <Button
                bg="#B59143"
                color="#1A1C1F"
                _hover={{ bg: "#A88238" }}
                border="1px solid"
                borderColor="#B59143"
                isLoading={isSaving}
                onClick={async () => {

                  if (pendingLevel && selectedEntryId) {
                    setIsSaving(true);
                    // optimistic local update
                    if (pendingUser) {
                      setStrike(pendingUser, pendingLevel);
                      setRemarksByUser((prev) => ({
                        ...prev,
                        [pendingUser]: {
                          ...(prev[pendingUser] || {}),
                          [pendingLevel]: pendingRemark,
                        },
                      }));
                    } else {
                      setAllTo(pendingLevel);
                      // apply same remark to all users at this level (optional)
                      setRemarksByUser((prev) => {
                        const next = { ...prev } as Record<
                          string,
                          { 1?: string; 2?: string; 3?: string }
                        >;
                        assignees.forEach((a) => {
                          next[a.id] = {
                            ...(next[a.id] || {}),
                            [pendingLevel]: pendingRemark,
                          };
                        });
                        return next;
                      });
                    }

                    // persist to Firestore (merge)
                    try {
                      const metaRef = doc(
                        db,
                        `organization/${organizationId}/projects/${projectId}/channels/${channelId}/tasks/${taskId}/strikes/${selectedEntryId}`
                      );
                      const payload: any = {
                        levels: pendingUser
                          ? {
                            [pendingUser]:
                              (strikeLevelsByUser[pendingUser] || 0) <
                                pendingLevel
                                ? pendingLevel
                                : strikeLevelsByUser[pendingUser] ||
                                pendingLevel,
                          }
                          : assignees.reduce((acc, a) => {
                            const current = strikeLevelsByUser[a.id] || 0;
                            acc[a.id] =
                              current < pendingLevel ? pendingLevel : current;
                            return acc;
                          }, {} as Record<string, number>),
                        remarks: pendingUser
                          ? {
                            [pendingUser]: {
                              ...((remarksByUser[pendingUser] as any) || {}),
                              [pendingLevel]: pendingRemark,
                            },
                          }
                          : assignees.reduce((acc, a) => {
                            acc[a.id] = {
                              ...((remarksByUser[a.id] as any) || {}),
                              [pendingLevel]: pendingRemark,
                            };
                            return acc;
                          }, {} as Record<string, { 1?: string; 2?: string; 3?: string }>),
                        updatedAt: serverTimestamp(),
                      };
                      await setDoc(metaRef, payload, { merge: true });

                      // Send strike email(s)
                      const levelLabel =
                        pendingLevel === 1
                          ? "Reminder"
                          : pendingLevel === 2
                            ? "Warning"
                            : "Final Warning";
                      const sendNotice = async (userId: string) => {
                        const u = users.find((x) => x.id === userId);
                        if (!u || !u.email) return;
                        const body = {
                          email: u.email,
                          firstName: u.firstName,
                          lastName: u.lastName,
                          taskName: selectedEntry?.name || "",
                          plannedStart:
                            (selectedEntry as any)?.plannedStart || "",
                          plannedEnd: (selectedEntry as any)?.plannedEnd || "",
                          strikeLevel: pendingLevel,
                          remark: pendingRemark,
                          levelLabel,
                        };



                        await axios
                          .post(`${backendUrl}/api/strike/strike-notice`, body)
                          .catch((error) => {
                            console.error("Error sending strike notice:", error);
                          });
                      };

                      if (pendingUser) {
                        await sendNotice(pendingUser);
                      } else {
                        for (const a of assignees) {
                          await sendNotice(a.id);
                        }
                      }
                    } catch (e) {
                      console.error("Failed to save strikes:", e);
                    } finally {
                      setIsSaving(false);
                    }
                  }
                  onClose();
                  setPendingRemark("");
                  setPendingLevel(null);
                  setPendingUser(null);
                }}
                isDisabled={pendingRemark.trim().length === 0}
              >
                Save
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </div>
  );
};

export default StrikeUser;